<template>
    <VueDraggable
        v-bind="listData"
        class="sbs-draggable-list"
        item-key="id"
        @end="dragEndHandler"
        @start="dragStartHandler">
        <SbsDraggableItem
            v-for="(item, i) in draggableModel"
            :key="item.id || i"
            class="sbs-draggable-list__item"
            v-bind="getItemData(item, i)"
            @end="dragEndHandler"
            @start="dragStartHandler">
            <template #accordionButtonIcon>
                <!-- @slot Слот для иконки кнопки аккордеона -->
                <slot name="accordionButtonIcon"></slot>
            </template>

            <template #itemTypeIcon>
                <!-- @slot Слот для иконки типа элемента -->
                <slot name="itemTypeIcon"></slot>
            </template>

            <template #default="slotProps">
                <!-- @slot Слот по-умолчанию -->
                <slot v-bind="slotProps"></slot>
            </template>
        </SbsDraggableItem>
    </VueDraggable>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep.js';
import { VueDraggable } from 'vue-draggable-plus';
import SbsDraggableItem from './SbsDraggableItem.vue';

/**
 * Список элементов с возможностью перетаскивания
 */
export default {
    name: 'SbsDraggableList',
    emits: ['update:modelValue', 'start', 'end'],
    components: {
        VueDraggable,
        SbsDraggableItem,
    },
    data() {
        return {
            draggedItem: null,
            isDropEnabled: null,
            whileDragging: false,
        };
    },
    computed: {
        draggableModel: {
            get() {
                return this.modelValue;
            },
            set(value) {
                /**
                * Эмит события `update:modelValue`. Срабатывает при вставке элемента в новое место (срабатывает до события `end`)
                */
                this.$emit('update:modelValue', value);
            },
        },
        listData() {
            return {
                'animation': 150,
                'class': {
                    'while-dragging': this.whileDragging,
                },
                'data-level': this.level,
                'data-type': 'list',
                'dragClass': 'sbs-draggable-item--drag',
                'invertSwap': true,
                'fallbackOnBody': true,
                'forceFallback': true,
                'ghostClass': 'sbs-draggable-item--ghost',
                'group': {
                    pull: this.pullHandler,
                    put: this.putHandler,
                },
                'handle': '.draghandle',
                'list': this.draggableModel,
                'swapThreshold': 0.3,
                'tag': 'ul',
            };
        },
    },
    methods: {
        dragEndHandler(evt) {
            this.updateModel();
            this.whileDragging = false;
            this.draggedItem = null;
            /**
             * Эмит события `end`. Происходит при завершении перетаскивания элемента.
             */
            this.$emit('end', evt);
        },
        dragStartHandler(evt) {
            this.whileDragging = true;
            this.draggedItem = evt.item;
            this.draggedItem.draggable = true;
            /**
             * Эмит события `start`. Происходит при начале перетаскивания элемента.
             */
            this.$emit('start', evt);
        },
        getItemData(item, index) {
            return {
                draggedItem: this.draggedItem,
                item,
                index,
                maxNestedDepth: this.maxNestedDepth,
                whileDragging: this.whileDragging,
            };
        },
        pullHandler() {
            return true;
        },
        putHandler(targetEl, _fromEl, draggedEl) {
            return this.toggleDrag(targetEl, draggedEl);
        },
        recursiveWalk(array, counter = 0, parent = null) {
            array.forEach(item => {
                item.storage = {};
                item.parent = parent;
                item.level = counter;
                item.depth = 0;

                if (item.type === 'folder' && parent) {
                    let p = parent;

                    while (p) {
                        if (!p.storage[item.level]) {
                            p.storage[item.level] = true;
                            p.depth += 1;
                        }

                        p = p.parent;
                    }
                }

                if (item?.items?.length) {
                    this.recursiveWalk(item.items, counter + 1, item);
                }
            });

            return array;
        },
        toggleDrag(targetEl, draggedEl) {
            const targetElementData = targetEl.el.dataset;
            const draggedElementData = draggedEl.dataset;
            const isDraggedFolder = draggedElementData.type === 'folder';
            const isTargetRootFolder = typeof targetElementData.level === 'undefined';
            const summDepthLevel = Number(targetElementData.level) + Number(draggedElementData.depth);
            const isDropEnabled = isDraggedFolder && summDepthLevel < this.maxNestedDepth;

            if (
                !isDraggedFolder ||
                this.maxNestedDepth < 0 ||
                (this.maxNestedDepth >= 0 && isTargetRootFolder) ||
                (this.maxNestedDepth > 0 && isDropEnabled)
            ) {
                this.isDropEnabled = true;

                return true;
            }

            return false;
        },
        updateModel() {
            const copyArray = cloneDeep(this.draggableModel);

            this.draggableModel = this.recursiveWalk(copyArray);
        },
    },
    props: {
        /**
         * В значении True, когда пользователь схватил и тянет элемент
         */
        isOuterItemDragging: {
            type: Boolean,
            default: false,
        },
        /**
         * Уровень вложенности элемента
         */
        level: {
            type: Number,
            default: null,
        },
        /**
         * Максимальный уровень(N) вложенности папки (data-type="folder")
         *
         * N < 0 — вложенность без ограничений
         *
         * 0 — нельзя вкладывать папки в другие папки
         *
         * N > 0 — разрешено N уровеней вложенности
         */
        maxNestedDepth: {
            type: Number,
            default: -1,
        },
        /**
         * Перемещаемый в данное время элемент
         */
        outerDraggedItem: {
            type: HTMLElement,
            default: null,
        },
        /**
         * Принимает массив с данными списка элементов
         */
        modelValue: {
            type: Array,
            default: () => [],
        },
    },
    watch: {
        isOuterItemDragging(val) {
            this.whileDragging = val;
        },
        outerDraggedItem(val) {
            this.draggedItem = val;
        },
    },
    mounted() {
        this.updateModel();
    },
};
</script>

.<style lang="scss">
.sbs-draggable-list {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    padding-left: 0;
    border-radius: $rounded-15;
    transition: $trs;

    &.while-dragging {
        cursor: move;
    }

    &:not([data-level]) {
        overflow: hidden;
        background-color: $gray-light;
        border: 1px solid $gray-light;
    }
}
</style>
